
import React from 'react';
import { BarChart2, TrendingUp, Zap, MessageSquare, ChevronRight, Loader2, Newspaper, Trophy, Clock } from 'lucide-react';
import { CommentWithUser, Language } from '../../types';
import { calculateTimeAgo } from '../../services/formatService';
import { t } from '../../i18n';

interface ActivityDashboardProps {
  activities: (CommentWithUser & { activityType?: 'match' | 'news' })[];
  filterPeriod: 'today' | '7days' | '30days' | 'all';
  onPeriodChange: (p: any) => void;
  isLoading: boolean;
  lang: Language;
}

export const ActivityDashboard: React.FC<ActivityDashboardProps> = ({
  activities, filterPeriod, onPeriodChange, isLoading, lang
}) => {
  const filtered = activities.filter(c => {
    if (filterPeriod === 'all') return true;
    const diff = Math.abs(Date.now() - new Date(c.created_at).getTime());
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (filterPeriod === 'today') return days <= 1;
    if (filterPeriod === '7days') return days <= 7;
    if (filterPeriod === '30days') return days <= 30;
    return true;
  });

  const mostActivePostId = filtered.length > 0 
    ? Object.entries(filtered.reduce((acc, c) => ({ ...acc, [c.post_id]: (acc[c.post_id] || 0) + 1 }), {} as Record<string, number>))
        .sort((a, b) => (b[1] as number) - (a[1] as number))[0][0]
    : null;

  return (
    <div className="space-y-0 animate-in fade-in duration-500 pb-20 bg-white">
      {/* Overview Grid */}
      <div className="p-6 grid grid-cols-2 gap-4 border-b border-gray-100 bg-slate-50/50">
        <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center transform transition-transform md:hover:scale-[1.02]">
          <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center mb-3">
            <BarChart2 size={24} className="text-rose-500" />
          </div>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t(lang, 'totalInteractions')}</span>
          <span className="text-2xl font-black text-slate-900 tabular-nums">{filtered.length}</span>
        </div>
        <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center transform transition-transform md:hover:scale-[1.02]">
          <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mb-3">
            <TrendingUp size={24} className="text-indigo-500" />
          </div>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t(lang, 'hotPost')}</span>
          <span className="text-sm font-black text-slate-900 truncate w-full px-2 uppercase">{mostActivePostId || '-'}</span>
        </div>
      </div>

      {/* Tabs Row */}
      <div className="bg-white/95 backdrop-blur-md sticky top-0 z-20 px-6 py-4 flex gap-2 overflow-x-auto no-scrollbar border-b border-gray-100 shadow-sm">
        {(['all', 'today', '7days', '30days'] as const).map((p) => (
          <button
            key={p}
            onClick={() => onPeriodChange(p)}
            className={`shrink-0 px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest transition-all ${
              filterPeriod === p 
              ? 'bg-slate-900 text-white shadow-lg' 
              : 'bg-white text-slate-400 border border-gray-100 md:hover:border-slate-300'
            }`}
          >
            {t(lang, p === 'all' ? 'allTime' : p === 'today' ? 'today' : p === '7days' ? 'last7Days' : 'last30Days')}
          </button>
        ))}
      </div>

      {/* Activities List */}
      <div className="divide-y divide-gray-50">
        {isLoading ? (
          <div className="py-32 flex flex-col items-center gap-4">
            <Loader2 className="animate-spin text-rose-500" size={32} />
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 animate-pulse">{t(lang, 'syncingActivities')}</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-40 text-center flex flex-col items-center gap-6 px-10">
            <div className="w-24 h-24 bg-slate-50 rounded-[48px] flex items-center justify-center text-slate-200 shadow-inner">
                <MessageSquare size={48} />
            </div>
            <div className="space-y-2">
              <p className="text-slate-900 font-black uppercase tracking-widest text-sm">{t(lang, 'noSectorActivity')}</p>
              <p className="text-slate-400 text-xs font-medium leading-relaxed">
                {t(lang, 'noSectorActivityDesc')}
              </p>
            </div>
            <button 
              onClick={() => window.location.hash = '/news'}
              className="mt-4 px-8 py-3 bg-slate-900 text-white rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg active:scale-95 transition-transform"
            >
              {t(lang, 'discoverMore')}
            </button>
          </div>
        ) : (
          filtered.map(item => (
            <div 
              key={`${item.activityType}-${item.id}`} 
              className="p-6 md:hover:bg-slate-50/80 transition-all cursor-pointer group animate-in fade-in border-b border-gray-50 last:border-0"
              onClick={() => {
                if (item.activityType === 'news') {
                  window.location.hash = `/news/item/${item.post_id}`;
                } else {
                  window.location.hash = `/game/${item.post_id}`;
                }
              }}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${item.activityType === 'news' ? 'bg-indigo-50 text-indigo-500' : 'bg-rose-50 text-rose-500'}`}>
                    {item.activityType === 'news' ? <Newspaper size={16} /> : <Trophy size={16} />}
                  </div>
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-black uppercase tracking-tight ${item.activityType === 'news' ? 'text-indigo-600' : 'text-rose-600'}`}>
                      {item.activityType === 'news' ? t(lang, 'intelligenceReport') : `${t(lang, 'matchIntel')} • ID: ${item.post_id}`}
                    </span>
                    {item.activityType === 'news' && item.lang && (
                      <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest mt-0.5">{item.lang === 'ko' ? t(lang, 'koreanFeed') : t(lang, 'englishFeed')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-slate-300">
                  <Clock size={12} />
                  <span className="text-[10px] font-black uppercase tabular-nums">
                    {calculateTimeAgo(item.created_at)}
                  </span>
                </div>
              </div>

              <div className="flex items-start justify-between gap-6 pl-10.5">
                <p className="text-slate-800 font-bold text-[15px] leading-relaxed flex-1 group-hover:text-slate-900 transition-colors">
                  {item.content}
                </p>
                <div className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center text-slate-300 md:group-hover:text-rose-500 md:group-hover:bg-rose-50 md:group-hover:border-rose-100 transition-all shrink-0">
                  <ChevronRight size={20} strokeWidth={3} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
